import React, { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { findSelectedWork } from "@/lib/pulseConstants";
import { trackViewProject } from "@/lib/analytics";

export default function ProjectDetailPage() {
    const { slug } = useParams();
    const project = findSelectedWork(slug);
    const viewFiredRef = useRef(null);

    useEffect(() => {
        if (project) {
            document.title = `${project.title} — REFORM Pulse`;
            // Fire view_project once per slug per mount cycle. The ref guard
            // is a defensive backstop against React 18 dev StrictMode double
            // invocation; in production it also protects against re-renders
            // due to unrelated state changes.
            if (viewFiredRef.current !== project.slug) {
                viewFiredRef.current = project.slug;
                trackViewProject({
                    project_slug: project.slug,
                    project_number: project.number,
                    source: "project_detail_page",
                });
            }
        } else {
            document.title = "Project not found — REFORM Pulse";
        }
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [project]);

    if (!project) {
        return (
            <section className="section-pad" data-testid="project-detail-not-found">
                <div className="container-editorial text-center">
                    <div className="eyebrow mb-4">// Not found</div>
                    <h1 className="display-2 text-white mb-6">This project isn’t <span className="text-[#FF2A2A]">public</span> yet.</h1>
                    <p className="body-lg max-w-lg mx-auto mb-8">
                        The case study you’re looking for either doesn’t exist or is still being prepared.
                    </p>
                    <Button asChild className="bg-[#FF2A2A] hover:bg-[#ff4747] text-black rounded-full h-12 px-6">
                        <Link to="/work" className="inline-flex items-center gap-2">
                            <ArrowLeft size={16} /> Back to Work
                        </Link>
                    </Button>
                </div>
            </section>
        );
    }

    return (
        <div data-testid={`project-detail-${project.slug}`}>
            {/* Hero */}
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <div
                        className="absolute inset-0 bg-cover"
                        style={{
                            backgroundImage: `url('${project.primary_image}')`,
                            backgroundPosition: project.primary_image_position || "center",
                            filter: "blur(28px) brightness(0.35) saturate(0.9)",
                            transform: "scale(1.12)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#07070A]/85" />
                    <div className="absolute inset-0 hero-radial" />
                </div>

                <div className="container-editorial">
                    <div className="mb-8">
                        <Link
                            to="/work"
                            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest transition-colors"
                            data-testid="project-detail-back-link"
                        >
                            <ArrowLeft size={14} /> Back to Work
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="font-display text-5xl md:text-6xl text-[#FF2A2A] leading-none">
                                    {project.number}
                                </span>
                                <div>
                                    <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/60">
                                        {project.client}
                                    </div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40 mt-1">
                                        Selected Work • REFORM Pulse
                                    </div>
                                </div>
                            </div>
                            <Reveal>
                                <h1 className="display-1 text-white text-balance">{project.title}</h1>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <div className="flex flex-wrap gap-2 mt-6">
                                    {project.categories.map((cat) => (
                                        <span
                                            key={cat}
                                            className="px-3 py-1.5 rounded-full border border-white/15 text-[10px] font-mono text-white/75 uppercase tracking-[0.2em]"
                                        >
                                            {cat}
                                        </span>
                                    ))}
                                </div>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="body-lg mt-8 max-w-2xl">{project.summary}</p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Primary image */}
            <section className="pb-12 md:pb-16">
                <div className="container-editorial">
                    <Reveal>
                        <div
                            className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0C10]"
                            style={{ aspectRatio: project.primary_image_aspect === "4 / 5" ? "16 / 10" : project.primary_image_aspect }}
                        >
                            <img
                                src={project.primary_image}
                                alt={project.primary_image_alt}
                                loading="eager"
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ objectPosition: project.primary_image_position || "center" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0 pointer-events-none" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Body content: challenge / approach / delivered */}
            <section className="section-pad border-t border-white/10">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-5">
                            <SectionEyebrow index="//" label="Project Detail" />
                            <Reveal>
                                <h2 className="display-3 text-white text-balance">
                                    A brand moment built to be <span className="text-[#FF2A2A]">experienced</span> in person.
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-7 space-y-8">
                            <Reveal delay={0.1}>
                                <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-6 md:p-7">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#FF2A2A] mb-3">
                                        Challenge
                                    </div>
                                    <p className="body-lg">{project.challenge}</p>
                                </div>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-6 md:p-7">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#FF2A2A] mb-3">
                                        Experience Approach
                                    </div>
                                    <p className="body-lg">{project.approach}</p>
                                </div>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-6 md:p-7">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#FF2A2A] mb-4">
                                        What we delivered
                                    </div>
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {project.delivered.map((d) => (
                                            <li key={d} className="flex items-start gap-3 text-white/85">
                                                <Check size={16} className="text-[#FF2A2A] mt-0.5 shrink-0" />
                                                <span className="text-sm md:text-base">{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section className="section-pad border-t border-white/10">
                <div className="container-editorial">
                    <SectionEyebrow index="//" label="Gallery" />
                    <Reveal>
                        <h2 className="display-3 text-white text-balance mb-10">
                            On <span className="text-[#FF2A2A]">location</span>.
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <Reveal>
                            <figure
                                className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0C10]"
                                style={{ aspectRatio: project.primary_image_aspect }}
                                data-testid="project-gallery-primary"
                            >
                                <img
                                    src={project.primary_image}
                                    alt={project.primary_image_alt}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ objectPosition: project.primary_image_position || "center" }}
                                />
                            </figure>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <figure
                                className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0C10]"
                                style={{ aspectRatio: project.primary_image_aspect }}
                                data-testid="project-gallery-secondary"
                            >
                                <img
                                    src={project.secondary_image}
                                    alt={project.secondary_image_alt}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ objectPosition: project.secondary_image_position || "center" }}
                                />
                            </figure>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="section-pad relative overflow-hidden border-t border-white/10">
                <motion.div
                    initial={{ opacity: 0.3 }}
                    whileInView={{ opacity: 0.85 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2 }}
                    className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-[#FF2A2A]/12 blur-3xl pointer-events-none"
                    aria-hidden="true"
                />
                <div className="container-editorial relative text-center">
                    <SectionEyebrow index="//" label="Next Up" className="justify-center" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Have a brand moment worth <span className="text-[#FF2A2A]">building</span>?
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg max-w-2xl mx-auto mt-6">
                            Send us your brief. We’ll come back with a first POV — concept fragments, venue ideas
                            and how we’d amplify it.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button
                                asChild
                                className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-14 px-8 text-base"
                                data-testid="project-detail-final-cta"
                            >
                                <Link to="/contact" className="inline-flex items-center gap-2">
                                    Start Your Pulse Brief <ArrowUpRight size={18} />
                                </Link>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="rounded-full border-white/25 bg-transparent hover:bg-white/5 text-white h-14 px-8 text-base"
                            >
                                <Link to="/work" className="inline-flex items-center gap-2">
                                    <ArrowLeft size={16} /> More Selected Work
                                </Link>
                            </Button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
